import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClientTable } from "../../../components/table/tableFeatures";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable, selectedIdsToSelectionState } from "../../../components/table/utils";
import { Select } from "../../../components/fields/base/Select";
import { type OrgUser } from "../../../zodios/apiDefinitions/api/userManagement";
import { DEFAULT_ROLE_NAME, ROLES } from "../constants";
import { MdRotateLeft } from "react-icons/md";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { RT8Pagination } from "../../../components/table/Pagination";
import { useAppSelector } from "../../../redux/hooks";
import { useBulkMutateUserRolesMutation } from "../api/useBulkMutateUserRolesMutation";
import { useOrgUsers } from "../../admin/api/useOrgUsers";
import AgencyFilter from "./AgencyFilter";
import Loading from "../../../components/core/blocks/Loading";
import { type RoleName, getRoleLabel } from "./OrgUsersTable";

const columnHelper = createColumnBuilder<OrgUser>();

const AddUsersToOrgModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation(["user_management", "common"]);
  const user = useAppSelector((state) => state.user);

  const defaultAgencyId = user.account_manager.agency_id ?? "none";
  const defaultShowAgencyFilter = user.is_staff && defaultAgencyId !== "none";

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [userRoles, setUserRoles] = useState<Record<number, string>>({});
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | "none">(defaultAgencyId);
  const [showAgencyFilter, setShowAgencyFilter] = useState(defaultShowAgencyFilter);

  const orgUsersQueryParams = useMemo(() => {
    const params: {
      include_staff: boolean;
      agency_id?: number;
    } = { include_staff: false };

    if (user.is_staff && selectedAgencyId !== "none") {
      params.agency_id = selectedAgencyId;
    }

    return params;
  }, [selectedAgencyId, user.is_staff]);

  const orgUsersQuery = useOrgUsers(orgUsersQueryParams);

  const userData: OrgUser[] = useMemo(() => {
    return orgUsersQuery.data?.filter((user) => user.is_active) ?? [];
  }, [orgUsersQuery.data]);

  const usersWithoutRoleInCurrentOrg = useMemo(() => {
    return userData.filter(
      (user) => user.roles.length === 0 || user.roles.every((role) => role.membership_status === "disabled"),
    );
  }, [userData]);

  const bulkMutateUserRolesMutation = useBulkMutateUserRolesMutation();

  const localizedRoles = useMemo(
    () =>
      ROLES.map((r) => ({
        label: getRoleLabel(t, r.value as RoleName),
        value: r.value,
      })),
    [t],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("first_name", {
        header: t(($) => $.user_management["Name"]),
        cell: (props) => (
          <span className={css({ paddingY: "small" })}>
            {props.row.original.first_name} {props.row.original.last_name}
          </span>
        ),
        size: 300,
      }),
      columnHelper.accessor("email", {
        header: t(($) => $.user_management["Email"]),
        size: 250,
      }),
      columnHelper.display({
        id: "role",
        header: t(($) => $.user_management["Role"]),
        size: 250,
        minSize: 250,
        maxSize: 250,
        cell: ({ row }) => {
          if (!row.getIsSelected()) return null;

          const userId = row.original.id;
          return (
            <Select
              items={localizedRoles}
              selectedKey={userRoles[userId]}
              onSelectionChange={(value) => {
                if (!value) return;
                setUserRoles((prev) => ({ ...prev, [userId]: String(value) }));
              }}
              css={{ width: "170px", paddingX: "xlarge" }}
            />
          );
        },
      }),
    ];
  }, [userRoles, t, localizedRoles]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "addUsersToOrgModalTable",
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      sorting: [{ id: "id", desc: false }],
      rowSelection: selectedIdsToSelectionState(selectedUserIds),
    },
  });

  const table = useClientTable({
    data: usersWithoutRoleInCurrentOrg,
    columns,
    initialState: initialState,
    getRowId: (row) => row.id.toString(),
    enableGlobalFilter: true,
    enableRowSelection: true,
    state: {
      ...state,
      rowSelection: selectedIdsToSelectionState(selectedUserIds),
    },
    ...setters,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.state.rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      setSelectedUserIds(updatedIds.map((id) => parseInt(id)));
      setUserRoles((prev) => {
        const newRoles = { ...prev };
        updatedIds.forEach((id) => {
          const intId = parseInt(id);
          if (!newRoles[intId]) {
            newRoles[intId] = DEFAULT_ROLE_NAME;
          }
        });
        return newRoles;
      });
    },
  });

  const isConfirmDisabled = selectedUserIds.length === 0 || selectedUserIds.some((userId) => !userRoles[userId]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      css={{ width: "60vw", overflowY: "auto" }}
      title={t(($) => $.user_management["Add Users to Current Organization"])}
    >
      <VStack
        className={css({
          width: "800px",
          marginX: "auto",
          padding: "large",
          overflowY: "scroll",
        })}
      >
        {user.is_staff && (
          <AgencyFilter
            show={showAgencyFilter}
            value={selectedAgencyId}
            onToggle={() => {
              setSelectedUserIds([]);
              setUserRoles({});
              setSelectedAgencyId("none");
              setShowAgencyFilter((prev) => !prev);
            }}
            onChange={(agencyId) => {
              setSelectedUserIds([]);
              setUserRoles({});
              setSelectedAgencyId(agencyId);
            }}
          />
        )}

        {orgUsersQuery.isLoading ?
          <Loading fullPage={false} height={"250px"} />
        : usersWithoutRoleInCurrentOrg.length === 0 ?
          <VStack justifyContent={"center"} alignItems={"center"}>
            <p>{t(($) => $.user_management["All users already have roles in this organization."])}</p>
            <Flex gap="xlarge" justifyContent="flex-end" paddingTop={"large"}>
              <Button onPress={onClose} variant="primary">
                {t(($) => $.common.actions.Close)}
              </Button>
            </Flex>
          </VStack>
        : <>
            <HStack width={"100%"} padding={"small"} fontSize={"large"}>
              <VStack flex="1" alignItems={"start"}>
                <HStack width={"100%"}>
                  <Button
                    variant={"contentOnly"}
                    onPress={() => {
                      resetTable(table);
                    }}
                    fontSize={"xxlarge"}
                  >
                    <MdRotateLeft />
                  </Button>
                  <SearchInput
                    aria-label={t(($) => $.common.actions.Search)}
                    isExpandable
                    css={{ flex: { base: "0 1 150px", md: "0 1 350px" } }}
                    value={state.globalFilter ?? ""}
                    onChange={(value) => {
                      setters.onGlobalFilterChange(value);
                    }}
                  />
                </HStack>
              </VStack>
              <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
            </HStack>
            <Table table={table} className={css({ maxWidth: "800px" })} />
            <Flex gap="xlarge" justifyContent="flex-end" paddingTop={"large"}>
              <Button onPress={onClose} variant={"secondary"}>
                {t(($) => $.common.actions.Cancel)}
              </Button>
              <Button
                variant="primary"
                isDisabled={isConfirmDisabled}
                onPress={() => {
                  const body = {
                    upserts: Object.entries(userRoles).map(([userId, roleName]) => ({
                      user_id: Number(userId),
                      org_id: Number(user.organization_id),
                      role_name: roleName,
                    })),
                    creates: [],
                    updates: [],
                    deletes: [],
                  };

                  bulkMutateUserRolesMutation.mutate(body, {
                    onSuccess: () => onClose(),
                  });
                }}
              >
                {t(($) => $.user_management["Confirm"])}
              </Button>
            </Flex>
          </>
        }
      </VStack>
    </Modal>
  );
};

export default AddUsersToOrgModal;
